import type { Partner } from "@/modules/partner";

export const LAST_USED_LOGIN_KEY = "lastUsedLogin";
export const PARTNER_KEY = "referralPartner";
export const PENDING_SIGNUP_PROFILE_KEY = "pendingSignupProfile";

export type LoginMethod = "email" | "google";

export interface LastUsedLogin {
	method: LoginMethod;
	email?: string;
}

export function getLastUsedLogin(): LastUsedLogin | null {
	try {
		const stored = localStorage.getItem(LAST_USED_LOGIN_KEY);
		if (!stored) return null;
		return JSON.parse(stored) as LastUsedLogin;
	} catch {
		return null;
	}
}

export function saveLastUsedLogin(data: LastUsedLogin) {
	localStorage.setItem(LAST_USED_LOGIN_KEY, JSON.stringify(data));
}

export function LastUsedBadge({ className = "", mobileClassName = "" }: { className?: string; mobileClassName?: string }) {
	return (
		<>
			{/* Mobile: positioned above the element */}
			<span
				className={`absolute sm:hidden inline-flex items-center px-2 py-0.5 rounded border border-gray-300 bg-white text-xs font-medium text-gray-700 shadow-sm whitespace-nowrap ${mobileClassName}`}
			>
				LAST USED
			</span>
			{/* Desktop: tooltip-style badge to the right */}
			<span
				className={`absolute -right-3 translate-x-full hidden sm:inline-flex items-center px-2.5 py-1 rounded border border-gray-300 bg-white text-xs font-medium text-gray-700 shadow-sm whitespace-nowrap ${className}`}
			>
				<span className="absolute -left-[6px] top-1/2 -translate-y-1/2 w-0 h-0 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent border-r-[6px] border-r-gray-300" />
				<span className="absolute -left-[5px] top-1/2 -translate-y-1/2 w-0 h-0 border-t-[5px] border-t-transparent border-b-[5px] border-b-transparent border-r-[5px] border-r-white" />
				LAST USED
			</span>
		</>
	);
}

export function PartnerBadge({ partner }: { partner: Partner }) {
	return (
		<div
			className="flex items-center gap-3 rounded px-4 py-3 mb-6 shadow-xs"
			style={{
				backgroundColor: partner.brand_color,
				color: partner.brand_color_scheme === "dark" ? "#ffffff" : "#1a1a1a",
				border: `1px solid color-mix(in oklch, ${partner.brand_color} 90%, black)`,
			}}
		>
			{partner.logo_url && <img src={partner.logo_url} alt={partner.name} className="h-8 object-contain shrink-0" />}
			<p className="text-sm">
				Referred by <span className="font-semibold">{partner.name}</span> for exclusive pricing &amp; perks
			</p>
		</div>
	);
}

export function PartnerBadgeSkeleton() {
	return (
		<div className="flex items-center gap-3 rounded-lg border border-gray-100 bg-gray-50/50 px-4 py-3 mb-6 animate-pulse">
			<div className="h-8 w-8 rounded-full bg-gray-200 shrink-0" />
			<div className="h-4 w-48 rounded bg-gray-200" />
		</div>
	);
}
